import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import apiClient from '../api/client';
import { Receipt, Search, Plus, Filter, X, FileText, Download, CheckCircle, XCircle, Clock, DollarSign, Users, User, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, Inbox, Coins, Wallet, ChevronLeft } from 'lucide-react';
import { formatCurrencyAmount } from '../components/CurrencyDisplay';
import TablePagination from '../components/TablePagination';

const EXPENSE_PAGE_SIZE = 25;

export default function ExpenseList() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if user can see all expenses
  const canViewAll = hasPermission('view_all_expenses') || hasPermission('manage_expenses');

  // View mode: 'mine' or 'all' (default based on URL or 'mine')
  const initialView = searchParams.get('view') === 'all' && canViewAll ? 'all' : 'mine';
  const [viewMode, setViewMode] = useState(initialView);

  // Sync view mode when URL changes (sidebar navigation)
  useEffect(() => {
    const urlView = searchParams.get('view') === 'all' && canViewAll ? 'all' : 'mine';
    setViewMode(urlView);
  }, [searchParams, canViewAll]);

  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [expPage, setExpPage] = useState(1);

  // Sync search param from URL (e.g. from global search navigation)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch) setSearchTerm(urlSearch);
  }, [searchParams]);

  // Sync status filter from URL (e.g. from notification task link)
  useEffect(() => {
    setStatusFilter(searchParams.get('status') || '');
  }, [searchParams]);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [sortField, setSortField] = useState('ExpenseDate');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchExpenses();
  }, [viewMode, statusFilter, departmentFilter, employeeFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchCategories();
    fetchDepartments();
    fetchEmployees();
  }, []);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = {};

      // Only send mine=true for "My Expenses" view
      if (viewMode === 'mine') {
        params.mine = 'true';
      }

      if (statusFilter) params.status = statusFilter;
      if (departmentFilter) params.departmentId = departmentFilter;
      if (employeeFilter && canViewAll) params.employeeId = employeeFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      
      const response = await apiClient.get('/expenses', { params });
      setExpenses(response.data.expenses || []);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/expenses/categories/list');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await apiClient.get('/departments');
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await apiClient.get('/users');
      setEmployees(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDepartmentFilter('');
    setEmployeeFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const handleViewChange = (mode) => {
    setViewMode(mode);
    // Update URL so sidebar active state stays in sync
    if (mode === 'all') {
      navigate('/expenses?view=all', { replace: true });
    } else {
      navigate('/expenses', { replace: true });
    }
    // Reset employee filter when switching views
    setEmployeeFilter('');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Draft': { bg: 'bg-gray-100', text: 'text-gray-800', icon: FileText },
      'Pending Approval': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      'Approved': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      'Rejected': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
      'Posted': { bg: 'bg-blue-100', text: 'text-blue-800', icon: DollarSign },
    };

    const config = statusConfig[status] || statusConfig['Draft'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const handleSort = useCallback((field) => {
    setSortDirection(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc');
    setSortField(field);
  }, [sortField]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-400" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  const filteredExpenses = useMemo(() => {
    let result = [...expenses];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(expense =>
        expense.ExpenseNumber?.toLowerCase().includes(term) ||
        expense.Purpose?.toLowerCase().includes(term) ||
        expense.EmployeeName?.toLowerCase().includes(term) ||
        expense.EmployeeEmail?.toLowerCase().includes(term) ||
        expense.DepartmentName?.toLowerCase().includes(term) ||
        String(expense.TotalAmount || '').includes(term)
      );
    }

    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (sortField === 'TotalAmount') {
        aVal = parseFloat(aVal) || 0; bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'ExpenseDate') {
        aVal = new Date(aVal || 0).getTime(); bVal = new Date(bVal || 0).getTime();
      } else {
        aVal = String(aVal || '').toLowerCase(); bVal = String(bVal || '').toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [expenses, searchTerm, sortField, sortDirection]);

  const pagedExpenses = useMemo(
    () => filteredExpenses.slice((expPage - 1) * EXPENSE_PAGE_SIZE, expPage * EXPENSE_PAGE_SIZE),
    [filteredExpenses, expPage]
  );

  // Reset page when search/filters change
  useEffect(() => { setExpPage(1); }, [searchTerm, statusFilter, departmentFilter, employeeFilter, dateFrom, dateTo, sortField, sortDirection]);

  const totalAmount = filteredExpenses.reduce((sum, exp) => sum + (parseFloat(exp.TotalAmount) || 0), 0);
  const isAllView = viewMode === 'all';
  const colSpan = isAllView ? 8 : 7;

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      {location.state?.from && (
        <button
          onClick={() => navigate(location.state.from)}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to {location.state.fromLabel || 'Previous Page'}
        </button>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="w-7 h-7 text-blue-600" />
            {isAllView ? 'All Expenses' : 'My Expenses'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isAllView ? 'View and manage all company expenses' : 'Submit and track your expense claims'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchExpenses}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          {viewMode === 'mine' && (
            <Link
              to="/expenses/submit"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Expense
            </Link>
          )}
        </div>
      </div>

      {/* View Toggle */}
      {canViewAll && (
        <div className="mb-6 flex bg-white rounded-lg border border-gray-200 p-1 w-fit">
          <button
            onClick={() => handleViewChange('mine')}
            className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'mine'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <User className="w-4 h-4 mr-2" />
            My Expenses
          </button>
          <button
            onClick={() => handleViewChange('all')}
            className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'all'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Users className="w-4 h-4 mr-2" />
            All Expenses
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Expenses</p>
              <p className="text-2xl font-bold text-gray-900">{filteredExpenses.length}</p>
            </div>
            <Wallet className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Amount</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrencyAmount(totalAmount, 'GBP')}</p>
            </div>
            <Coins className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Approval</p>
              <p className="text-2xl font-bold text-gray-900">{filteredExpenses.filter(e => e.Status === 'Pending Approval').length}</p>
            </div>
            <Clock className="w-8 h-8 text-amber-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Approved</p>
              <p className="text-2xl font-bold text-green-600">{filteredExpenses.filter(e => e.Status === 'Approved').length}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6 p-4">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by expense no, purpose, submitter, department, amount..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-4 py-2 border rounded-lg transition-colors ${
              (statusFilter || departmentFilter || employeeFilter || dateFrom || dateTo)
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:bg-gray-50 text-gray-700'
            }`}
          >
            <Filter className="w-5 h-5 mr-2" />
            Filters
            {(statusFilter || departmentFilter || employeeFilter || dateFrom || dateTo) && (
              <span className="ml-2 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {[statusFilter, departmentFilter, employeeFilter, dateFrom, dateTo].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="border-t pt-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="Draft">Draft</option>
                  <option value="Pending Approval">Pending Approval</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Posted">Posted</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submitted By</label>
                <select
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">All Submitters</option>
                  {employees.map(emp => (
                    <option key={emp.UserID} value={emp.UserID}>
                      {emp.Username || emp.Email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept.DepartmentID} value={dept.DepartmentID}>
                      {dept.DepartmentName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleClearFilters}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear All
                </button>
              </div>
            </div>

            {/* Active filter tags */}
            {(statusFilter || departmentFilter || employeeFilter || dateFrom || dateTo) && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-500 py-1">Active filters:</span>
                {statusFilter && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Status: {statusFilter}
                    <button onClick={() => setStatusFilter('')} className="ml-1.5 hover:text-blue-600"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {employeeFilter && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Submitter: {employees.find(e => e.UserID === employeeFilter)?.Username || employees.find(e => e.UserID === employeeFilter)?.Email || 'Selected'}
                    <button onClick={() => setEmployeeFilter('')} className="ml-1.5 hover:text-blue-600"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {departmentFilter && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Dept: {departments.find(d => d.DepartmentID === departmentFilter)?.DepartmentName || 'Selected'}
                    <button onClick={() => setDepartmentFilter('')} className="ml-1.5 hover:text-blue-600"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {dateFrom && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    From: {dateFrom}
                    <button onClick={() => setDateFrom('')} className="ml-1.5 hover:text-blue-600"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {dateTo && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    To: {dateTo}
                    <button onClick={() => setDateTo('')} className="ml-1.5 hover:text-blue-600"><X className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Expenses Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('ExpenseNumber')}>
                  <span className="inline-flex items-center">Expense # <SortIcon field="ExpenseNumber" /></span>
                </th>
                {isAllView && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('EmployeeName')}>
                    <span className="inline-flex items-center">Submitted By <SortIcon field="EmployeeName" /></span>
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('ExpenseDate')}>
                  <span className="inline-flex items-center">Date <SortIcon field="ExpenseDate" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purpose
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('DepartmentName')}>
                  <span className="inline-flex items-center">Department <SortIcon field="DepartmentName" /></span>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('TotalAmount')}>
                  <span className="inline-flex items-center justify-end">Amount <SortIcon field="TotalAmount" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Status')}>
                  <span className="inline-flex items-center">Status <SortIcon field="Status" /></span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Receipts
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: colSpan }).map((__, ci) => (
                      <td key={ci} className="px-4 py-3">
                        <div className="h-4 skeleton-shimmer rounded" style={{ width: ci === 0 ? '80%' : ci % 2 === 0 ? '50%' : '65%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-12 text-center">
                    <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 font-medium">
                      {searchTerm || statusFilter || departmentFilter || employeeFilter || dateFrom || dateTo
                        ? 'No expenses match your filters'
                        : isAllView ? 'No expenses found' : 'No expenses yet'}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      {!isAllView && !searchTerm && 'Click "New Expense" to submit your first claim'}
                      {(searchTerm || statusFilter || departmentFilter) && 'Try adjusting your filters'}
                    </p>
                  </td>
                </tr>
              ) : (
                pagedExpenses.map((expense) => (
                  <tr key={expense.ExpenseID} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link
                        to={`/expenses/${expense.ExpenseID}`}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        {expense.ExpenseNumber}
                      </Link>
                    </td>
                    {isAllView && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-7 w-7 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-3.5 h-3.5 text-blue-600" />
                          </div>
                          <div className="ml-2">
                            <div className="text-sm font-medium text-gray-900">{expense.EmployeeName}</div>
                            <div className="text-xs text-gray-500">{expense.EmployeeEmail}</div>
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {new Date(expense.ExpenseDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="max-w-[200px] truncate" title={expense.Purpose}>
                        {expense.Purpose}
                      </div>
                      {expense.Category && (
                        <div className="text-xs text-gray-500">{expense.Category}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {expense.DepartmentName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                      {formatCurrencyAmount(expense.TotalAmount, expense.Currency)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getStatusBadge(expense.Status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center text-sm">
                      {expense.DocumentCount > 0 ? (
                        <span className="inline-flex items-center text-green-600">
                          <FileText className="w-4 h-4 mr-1" />
                          {expense.DocumentCount}
                        </span>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePagination page={expPage} pageSize={EXPENSE_PAGE_SIZE} total={filteredExpenses.length} onPageChange={setExpPage} />
      </div>
    </div>
  );
}
